import type { Assets } from '../core/Assets';
import type { DocSpec, DocField, Decision } from '../game/data/types';

export interface FieldRect {
  field: DocField;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DocRender {
  canvas: HTMLCanvasElement;
  fieldRects: FieldRect[];
  /** Seal hit zone, if the doc carries one. */
  sealRect: { x: number; y: number; w: number; h: number } | null;
  /** Stamp landing zone (decision slip only). */
  stampRect: { x: number; y: number; w: number; h: number } | null;
}

/** Curved wordmark along a circular band. */
function arcText(
  ctx: CanvasRenderingContext2D,
  text: string,
  radius: number,
  start: number,
  end: number,
  size: number,
  color: string,
  bottom: boolean,
): void {
  ctx.save();
  ctx.fillStyle = color;
  ctx.font = `700 ${size}px Georgia, 'Times New Roman', serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const n = text.length;
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0.5 : i / (n - 1);
    const a = start + (end - start) * t;
    ctx.save();
    ctx.rotate(a);
    ctx.translate(0, -radius);
    if (bottom) ctx.rotate(Math.PI);
    ctx.fillText(text[i], 0, 0);
    ctx.restore();
  }
  ctx.restore();
}

/**
 * Global Interactive revenue-share seal. The seal certifies a studio pays the
 * corporation its cut, so it is what the desk actually checks for.
 * Authentic: brass foil, guilloche rosette ring, curved wordmark top and bottom,
 * embossed % monogram. forged-a drops detail (cheap knockoff); forged-b uses the
 * wrong ink and mark. Both stay visibly distinct so the forgery is spottable.
 */
export function drawSeal(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  kind: 'authentic' | 'forged-a' | 'forged-b',
): void {
  const forgedA = kind === 'forged-a';
  const forgedB = kind === 'forged-b';
  const pal = forgedB
    ? { hi: '#c79bd8', mid: '#8a4a9a', lo: '#552a63', ink: '#f2e6f6' }
    : forgedA
      ? { hi: '#cdbd86', mid: '#9a8850', lo: '#665a32', ink: '#f3ecd4' }
      : { hi: '#f0d78e', mid: '#c39a3e', lo: '#78591d', ink: '#fff8e2' };

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(-0.05);

  // Foil medallion with an off-axis sheen.
  const base = ctx.createRadialGradient(-r * 0.32, -r * 0.36, r * 0.08, 0, 0, r);
  base.addColorStop(0, pal.hi);
  base.addColorStop(0.55, pal.mid);
  base.addColorStop(1, pal.lo);
  ctx.fillStyle = base;
  ctx.globalAlpha = 0.97;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;

  // Embossed rim: dark outer edge, bright inner highlight.
  ctx.lineWidth = Math.max(2, r * 0.05);
  ctx.strokeStyle = pal.lo;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.965, 0, Math.PI * 2); ctx.stroke();
  ctx.lineWidth = Math.max(1, r * 0.02);
  ctx.strokeStyle = pal.hi;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.9, 0, Math.PI * 2); ctx.stroke();

  // Ornamental ring between rim and wordmark.
  if (!forgedA) {
    // Authentic / wrong-ink: fine guilloche rosette.
    ctx.save();
    ctx.strokeStyle = pal.lo;
    ctx.globalAlpha = 0.5;
    ctx.lineWidth = Math.max(0.6, r * 0.012);
    const petals = 40;
    for (let i = 0; i < petals; i++) {
      const a = (i / petals) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(Math.cos(a) * r * 0.82, Math.sin(a) * r * 0.82, r * 0.085, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  } else {
    // Knockoff: crude ticks, no rosette.
    ctx.save();
    ctx.strokeStyle = pal.lo;
    ctx.globalAlpha = 0.55;
    ctx.lineWidth = Math.max(1, r * 0.03);
    const ticks = 16;
    for (let i = 0; i < ticks; i++) {
      const a = (i / ticks) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * r * 0.79, Math.sin(a) * r * 0.79);
      ctx.lineTo(Math.cos(a) * r * 0.88, Math.sin(a) * r * 0.88);
      ctx.stroke();
    }
    ctx.restore();
  }

  // Inner ring framing the monogram.
  ctx.lineWidth = Math.max(1.4, r * 0.03);
  ctx.strokeStyle = pal.lo;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.5, 0, Math.PI * 2); ctx.stroke();

  // Curved corporate wordmark.
  const bandR = r * 0.68;
  const fs = Math.max(6, r * 0.16);
  arcText(ctx, 'GLOBAL INTERACTIVE', bandR, -1.16, 1.16, fs, pal.ink, false);
  if (!forgedA) arcText(ctx, 'REVENUE SHARE', bandR, Math.PI + 0.95, Math.PI - 0.95, fs, pal.ink, true);

  // Center medallion with an embossed mark.
  const cg = ctx.createRadialGradient(-r * 0.12, -r * 0.14, r * 0.04, 0, 0, r * 0.46);
  cg.addColorStop(0, pal.mid);
  cg.addColorStop(1, pal.lo);
  ctx.fillStyle = cg;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.44, 0, Math.PI * 2); ctx.fill();

  const mark = forgedB ? '★' : '%';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `700 ${Math.round(r * 0.5)}px Georgia, 'Times New Roman', serif`;
  ctx.fillStyle = pal.lo;
  ctx.fillText(mark, r * 0.03, r * 0.06);
  ctx.fillStyle = pal.ink;
  ctx.fillText(mark, -r * 0.01, 0);

  ctx.restore();
}

const DOC_SIZES: Record<string, [number, number]> = {
  badge: [340, 440],
  paper: [420, 520],
  contract: [420, 560],
  coinbag: [300, 320],
  slip: [400, 460],
};

export function docWorldSize(spec: DocSpec): [number, number] {
  const [w, h] = DOC_SIZES[spec.kind] ?? DOC_SIZES.paper;
  return [w * 0.72, h * 0.72];
}

export function renderDoc(assets: Assets, spec: DocSpec, stamp?: Decision | null): DocRender {
  const [W, H] = DOC_SIZES[spec.kind] ?? DOC_SIZES.paper;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  const fieldRects: FieldRect[] = [];
  let sealRect: DocRender['sealRect'] = null;
  let stampRect: DocRender['stampRect'] = null;

  if (spec.kind === 'coinbag') {
    drawCoinBag(ctx, W, H, spec, fieldRects);
    return { canvas, fieldRects, sealRect, stampRect };
  }

  const bgKey = spec.kind === 'badge' ? 'prop:badge' : 'prop:paper-doc';
  const bg = assets.image(bgKey);
  if (bg) ctx.drawImage(bg, 0, 0, W, H);
  else {
    ctx.fillStyle = '#d8d2c0';
    ctx.fillRect(0, 0, W, H);
  }
  // Unify tone over the generated background so text always reads.
  ctx.fillStyle = 'rgba(226, 220, 202, 0.62)';
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = 'rgba(60, 55, 40, 0.55)';
  ctx.lineWidth = 3;
  ctx.strokeRect(4, 4, W - 8, H - 8);

  ctx.fillStyle = '#26231c';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';

  let y = 44;
  // Header band
  ctx.fillStyle = spec.kind === 'badge' ? '#3a4656' : '#4a4436';
  ctx.fillRect(10, 14, W - 20, 46);
  ctx.fillStyle = '#e8e2d0';
  ctx.font = 'bold 24px Courier New';
  ctx.fillText(fitText(ctx, spec.title, W - 36), 18, 44);
  y = 84;
  if (spec.subtitle) {
    ctx.fillStyle = '#4a4638';
    ctx.font = '17px Courier New';
    ctx.fillText(fitText(ctx, spec.subtitle, W - 36), 18, y);
    y += 17;
  }

  if (spec.kind === 'badge' && spec.photo) {
    const size = 150;
    const px = 20;
    const py = y + 8;
    const photo = assets.image(`portrait:${spec.photo}`);
    ctx.fillStyle = '#b8b2a0';
    ctx.fillRect(px - 3, py - 3, size + 6, size + 6);
    if (photo) {
      // Crop to head: portraits are busts, take upper-center square.
      const s = Math.min(photo.width, photo.height * 0.72);
      ctx.drawImage(photo, (photo.width - s) / 2, photo.height * 0.02, s, s, px, py, size, size);
    }
    ctx.strokeStyle = '#3a3628';
    ctx.lineWidth = 2;
    ctx.strokeRect(px - 3, py - 3, size + 6, size + 6);
    fieldRects.push({
      field: { id: 'photo', label: 'PHOTO', value: spec.photo, tag: 'face' },
      x: px, y: py, w: size, h: size,
    });
    y = py + size + 32;
  } else {
    y += 26;
  }

  ctx.font = '19px Courier New';
  for (const field of spec.fields) {
    const rowY = y;
    ctx.fillStyle = '#6a6250';
    ctx.font = '16px Courier New';
    ctx.fillText(field.label, 20, rowY);
    ctx.fillStyle = '#26231c';
    ctx.font = 'bold 21px Courier New';
    ctx.fillText(fitText(ctx, field.value, W - 44), 20, rowY + 25);
    ctx.strokeStyle = 'rgba(90, 84, 64, 0.4)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, rowY + 34);
    ctx.lineTo(W - 20, rowY + 34);
    ctx.stroke();
    fieldRects.push({ field, x: 14, y: rowY - 14, w: W - 28, h: 56 });
    y = rowY + 58;
  }

  if (spec.seal) {
    const r = 52;
    const cx = W - r - 30;
    const cy = H - r - 34;
    drawSeal(ctx, cx, cy, r, spec.seal);
    sealRect = { x: cx - r, y: cy - r, w: r * 2, h: r * 2 };
  }

  if (spec.kind === 'slip') {
    const zw = W - 60;
    const zh = 150;
    const zx = 30;
    const zy = H - zh - 30;
    ctx.setLineDash([8, 6]);
    ctx.strokeStyle = 'rgba(90, 84, 64, 0.6)';
    ctx.lineWidth = 2;
    ctx.strokeRect(zx, zy, zw, zh);
    ctx.setLineDash([]);
    if (!stamp) {
      ctx.fillStyle = 'rgba(90, 84, 64, 0.55)';
      ctx.font = '17px Courier New';
      ctx.textAlign = 'center';
      ctx.fillText('DECISION HERE', zx + zw / 2, zy + zh / 2 + 5);
      ctx.textAlign = 'left';
    }
    stampRect = { x: zx, y: zy, w: zw, h: zh };
    if (stamp) {
      const img = assets.image(stamp === 'greenlit' ? 'prop:stamp-greenlit' : 'prop:stamp-sunset');
      if (img) {
        ctx.save();
        ctx.globalCompositeOperation = 'multiply';
        ctx.globalAlpha = 0.92;
        ctx.translate(zx + zw / 2, zy + zh / 2);
        ctx.rotate((stamp === 'greenlit' ? -1 : 1) * 0.08);
        const s = zh * 1.18;
        ctx.drawImage(img, -s / 2, -s / 2, s, s);
        ctx.restore();
      } else {
        ctx.fillStyle = stamp === 'greenlit' ? '#3a7a42aa' : '#a8453caa';
        ctx.font = 'bold 46px Courier New';
        ctx.textAlign = 'center';
        ctx.fillText(stamp.toUpperCase(), zx + zw / 2, zy + zh / 2 + 12);
        ctx.textAlign = 'left';
      }
    }
  }

  return { canvas, fieldRects, sealRect, stampRect };
}

function drawCoinBag(
  ctx: CanvasRenderingContext2D,
  W: number,
  H: number,
  spec: DocSpec,
  fieldRects: FieldRect[],
): void {
  ctx.clearRect(0, 0, W, H);
  // Burlap sack
  ctx.fillStyle = '#7a5f3f';
  ctx.strokeStyle = '#4a3a26';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(W * 0.5, H * 0.12);
  ctx.bezierCurveTo(W * 0.28, H * 0.2, W * 0.1, H * 0.5, W * 0.18, H * 0.78);
  ctx.bezierCurveTo(W * 0.24, H * 0.95, W * 0.76, H * 0.95, W * 0.82, H * 0.78);
  ctx.bezierCurveTo(W * 0.9, H * 0.5, W * 0.72, H * 0.2, W * 0.5, H * 0.12);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // Tie
  ctx.fillStyle = '#4a3a26';
  ctx.fillRect(W * 0.42, H * 0.1, W * 0.16, H * 0.06);
  // CR symbol
  ctx.fillStyle = '#e8ce7a';
  ctx.font = `bold ${Math.round(H * 0.2) + 2}px Courier New`;
  ctx.textAlign = 'center';
  ctx.fillText('CR', W * 0.5, H * 0.62);
  // Declared tag
  const field = spec.fields[0];
  if (field) {
    ctx.fillStyle = '#ded8c6';
    ctx.fillRect(W * 0.2, H * 0.72, W * 0.6, H * 0.16);
    ctx.strokeStyle = '#4a3a26';
    ctx.lineWidth = 2;
    ctx.strokeRect(W * 0.2, H * 0.72, W * 0.6, H * 0.16);
    ctx.fillStyle = '#26231c';
    ctx.font = 'bold 19px Courier New';
    ctx.fillText(fitText(ctx, field.value, W * 0.6 - 10), W * 0.5, H * 0.82);
    fieldRects.push({ field, x: W * 0.2, y: H * 0.72, w: W * 0.6, h: H * 0.16 });
  }
  ctx.textAlign = 'left';
}

function fitText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 2 && ctx.measureText(`${t}…`).width > maxWidth) t = t.slice(0, -1);
  return `${t}…`;
}
